import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import Avatar from '../Avatar/Avatar';
import Col from '../../components/GridSystem/Col';
import { Card } from '../Card';
import CardTitle from '../Card/CardTitle';
import CardThumbnail from '../Card/CardThumbnail';
import styles from './CardCol.module.scss';
import Checkbox from '../Checkbox/Checkbox';
import DotsBtn from '../Menu/DotsBtn';
import CardFooter from '../Card/CardFooter';
import CardHeader from '../Card/CardHeader';
import CardExtra from '../Card/CardExtra';
import CardMenu from '../CardMenu/CardMenu';
import { useLibraryContext } from '../../context/LibraryContext';
import { formatSecondsToDuration, timeStampToMs } from '../../utils/TimeUtil';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { useUserContext } from '../../context/UserContext';
import { approvalStatuses } from '../../features/Dashboard/components/VideosFilter/constants';
import { parseString } from '../../utils/stringHelper';
import Tooltip from '../Tooltip/Tooltip';
import { MAX_NAME_LENGTH } from '../../../common/constants/common';
import { Link, useHistory } from 'react-router-dom';
import { useMountComponentWithDelay } from '../../hooks/useMountComponentWithDelay';
import FadeInComponent from '../FadeInComponent/FadeInComponent';
import { AnimationWrapper } from '../AnimationWrapper/AnimationWrapper';

const CardCol = ({
  menuRef,
  listMode,
  video,
  folder,
  videoIndex,
  searchInput,
  toggleShowMenu,
  renameVideoData,
  renameVideo,
  handleCopyClick,
  shareVideo,
  removeFromReview,
  duplicateVideo,
  downloadVideo,
  archiveVideos,
  moveVideoAction,
  unarchiveVideo,
  handleDotsClick,
  showMenu,
  onRenameInputChange,
  handleCardClick,
  deleteVideo,
  renameVideoAction,
  approveVideo,
  declineVideo,
  revokeVideo,
  isMobileView,
  squeezeCard,
  isUserHasEditingRights,
  isUserHasAdvancedEditingRights,
  isShowApprovalStatus,
  getRedirectLink,
  hideActions,
  alt = '',
}) => {
  const [hoverCard, setHoverCard] = useState({});
  const [showHoverState, setShowHoverState] = useState();
  const [titleHover, setTitleHover] = useState({});
  const [videoDuration, setVideoDuration] = useState(0);
  const [init, setInit] = useState(false);

  const { selectedCard } = useLibraryContext();

  const { user, isApprovalFlowEnabled, isReviewerUser } = useUserContext();

  const history = useHistory();

  const isVideoAuthor = useMemo(() => user?.username === parseString(video.author.username), [user, video]);
  const isUserCanDoAction = useMemo(() => isVideoAuthor || isUserHasEditingRights, [isVideoAuthor, isUserHasEditingRights]);
  const isUserCanDoAdvancedAction = useMemo(() => isVideoAuthor || isUserHasAdvancedEditingRights, [
    isVideoAuthor,
    isUserHasAdvancedEditingRights,
  ]);

  const isProcessing = useMemo(() => !video.draft && video.thumbnail === null, [video]);
  useMountComponentWithDelay(hoverCard.isHover, 250, () => {
    if (hoverCard.isHover) {
      setShowHoverState(true);
    }
  });

  /* These statuses are needed to display or hide action buttons if isApprovalFlowEnabled is true
   * 1) 'Rename button' will be displayed when video's approvalStatus is 'in Review' or 'Approved'
   * 2) 'Share button' will be displayed only when video's approvalStatus is 'Approved'
   * 3) 'Copy link button' will be displayed only when video's approvalStatus is 'Approved'
   * 4) Status in the header of card will be displayed if video's approvalStatus is not 'Concept'
   */

  const isPreExistedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.PRE_EXISTED.TYPE, [video]);
  const isInReviewStatus = useMemo(() => video?.approvalStatus === approvalStatuses.IN_REVIEW.TYPE, [video]);
  const isApprovedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.APPROVED.TYPE, [video]);

  const redirectLink = useMemo(() => getRedirectLink(video, folder), [video, folder]);

  const { t } = useTranslation();

  const showCheckbox = useMemo(() => !hideActions && hoverCard.isHover && hoverCard.index === videoIndex && !isMobileView, [
    hideActions,
    hoverCard,
    videoIndex,
    isMobileView,
  ]);

  useEffect(() => {
    if (video.scenes) {
      const trimStart = timeStampToMs(video.scenes[0].trimStart);
      const trimEnd = timeStampToMs(video.scenes[0].trimEnd);
      setVideoDuration(trimEnd - trimStart);
    } else {
      setVideoDuration(video.duration);
    }
  }, [video]);

  const toggleMouseEnterCard = videoIndex => {
    setHoverCard({ index: videoIndex, isHover: true });
  };

  const toggleMouseLeaveCard = videoIndex => {
    setHoverCard({ index: videoIndex, isHover: false });
  };

  const toggleMouseEnterTitle = videoIndex => {
    setTitleHover({ index: videoIndex, isHover: true });
  };

  const toggleMouseLeaveTitle = videoIndex => {
    setTitleHover({ index: videoIndex, isHover: false });
  };

  const onCardClick = event => {
    const classList = Array.from(event?.target?.parentElement?.classList).join('');

    const isTargetCard = classList.includes('Card');
    const isLinkClick = classList.includes('thumbnail');

    if (!redirectLink) {
      window.open(video?.videoLink, '_blank');
      return;
    }
    if (isTargetCard && !isLinkClick) {
      if (isMobileView) {
        history.push(redirectLink);
      } else {
        handleCardClick(event, videoIndex);
      }
    }
  };

  const handleShareVideo = shareVideo
    ? useCallback(
      id => {
        shareVideo(id);
        setHoverCard({});
      },
      [shareVideo, setHoverCard]
    )
    : undefined;

  useEffect(() => {
    setInit(true);
  }, []);

  return (
    <Col key={video.id} xs={12} sm={3} md={3} lg={3} xl={3} className={styles.cards}>
      <Card
        data-cy="video"
        onMouseEnter={() => toggleMouseEnterCard(videoIndex)}
        onMouseLeave={() => toggleMouseLeaveCard(videoIndex)}
        onClick={onCardClick}
        className={cx({
          [styles.activeCard]: selectedCard[video.id],
          [styles.card]: isMobileView,
          [styles.renameError]: renameVideoData[video.id]?.error,
          [styles.pointer]: hideActions,
        })}
      >
        <CardHeader>
          <CardTitle className={styles.relative}>
            <div className={cx(styles.absolute, styles.checkbox)}>
              <AnimationWrapper show={showCheckbox || selectedCard[video.id]} options={{ duration: 250 }}>
                <Checkbox onClick={event => handleCardClick(event, videoIndex)} checked={selectedCard[video.id]} />
              </AnimationWrapper>
            </div>
            <div className={cx(styles.avatarContainer, styles.absolute)}>
              <AnimationWrapper
                show={!showCheckbox && !selectedCard[video.id]}
                from={{ opacity: 0, ...(init ? { transform: 'scale(0.5)' } : {}) }}
                to={{ opacity: 1, ...(init ? { transform: 'scale(1)' } : {}) }}
                options={{ duration: 200 }}
                transformOrigin={'top left'}
              >
                <Avatar
                  className={styles.miniUserDetail}
                  image={video?.author?.avatar || undefined}
                  title={video?.author?.name}
                  size="small"
                  alt={alt}
                />
              </AnimationWrapper>
            </div>
            {/* </div> */}
          </CardTitle>
          {!hideActions && (
            <CardExtra className={cx({ [styles.cardExtra]: isMobileView })}>
              {(hoverCard.isHover && hoverCard.index === videoIndex) || selectedCard[video.id] || isMobileView ? (
                <FadeInComponent onHover={showHoverState}>
                  <DotsBtn tagName="BUTTON" onClick={() => handleDotsClick(video.id)} />
                </FadeInComponent>
              ) : null}
              {!isMobileView && showMenu.uuid === video.id && showMenu.flag ? (
                <CardMenu
                  menuRef={menuRef}
                  listMode={listMode}
                  item={video}
                  itemIndex={videoIndex}
                  toggleShowMenu={toggleShowMenu}
                  moveItem={isUserCanDoAdvancedAction && moveVideoAction}
                  renameItem={
                    !isProcessing &&
                    (!isApprovalFlowEnabled || (!isInReviewStatus && !isApprovedStatus)) &&
                    isUserCanDoAction &&
                    renameVideo
                  }
                  handleCopyClick={
                    !isProcessing &&
                    (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) &&
                    !video.draft &&
                    handleCopyClick
                  }
                  shareItem={
                    !isProcessing &&
                    (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) &&
                    !video.draft &&
                    handleShareVideo
                  }
                  removeFromReview={
                    !isProcessing && isApprovalFlowEnabled && isInReviewStatus && isUserCanDoAction && removeFromReview
                  }
                  duplicateItem={
                    !isProcessing && (video.author.username === user.username || isUserHasEditingRights) && duplicateVideo
                  }
                  downloadItem={
                    !isProcessing && {
                      action: (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && !video.draft && downloadVideo,
                    }
                  }
                  archiveItem={isVideoAuthor && archiveVideos}
                  deleteVideo={isUserCanDoAdvancedAction && deleteVideo}
                  unarchiveItem={isUserCanDoAction && unarchiveVideo}
                  approveVideo={!isProcessing && approveVideo}
                  declineVideo={!isProcessing && declineVideo}
                  revokeVideo={!isProcessing && revokeVideo}
                  rightOffset={true}
                />
              ) : null}
            </CardExtra>
          )}
        </CardHeader>
        {redirectLink ? (
          <Link to={redirectLink} className={styles.link}>
            <CardThumbnail
              childClassName={styles.customChildrenThumbnail}
              listMode={listMode}
              isHoverCard={hoverCard?.isHover && hoverCard?.index === videoIndex}
              src={video.thumbnail}
              alt="Click for Details"
              isMobileView={isMobileView}
              isDraft={video.draft}
              fadeInComponent={
                <FadeInComponent onHover={showHoverState}>
                  <div className={styles.duration}>
                    <span>{formatSecondsToDuration(videoDuration / 1000)}</span>
                  </div>
                  {!hideActions && (
                    <div className={styles.views}>
                      <span>
                        {video?.stats?.views || 0} {video?.stats?.views === 1 ? t('general.view') : t('general.views')}
                      </span>
                    </div>
                  )}
                </FadeInComponent>
              }
              isShowApprovalStatus={
                isShowApprovalStatus &&
                isApprovalFlowEnabled &&
                !isApprovedStatus &&
                !isPreExistedStatus &&
                (isUserCanDoAction || isReviewerUser)
              }
              approvalStatus={t(Object.values(approvalStatuses).find(({ TYPE }) => TYPE === video?.approvalStatus)?.DESCRIPTION)}
            />
          </Link>
        ) : (
          <CardThumbnail
            childClassName={styles.customChildrenThumbnail}
            listMode={listMode}
            isHoverCard={hoverCard?.isHover && hoverCard?.index === videoIndex}
            src={video.thumbnail}
            alt="Click for Details"
            isMobileView={isMobileView}
            isDraft={video.draft}
            fadeInComponent={
              <FadeInComponent onHover={showHoverState}>
                <div className={styles.duration}>
                  <span>{formatSecondsToDuration(videoDuration / 1000)}</span>
                </div>
                {!hideActions && (
                  <div className={styles.views}>
                    <span>
                      {video?.stats?.views || 0} {video?.stats?.views === 1 ? t('general.view') : t('general.views')}
                    </span>
                  </div>
                )}
              </FadeInComponent>
            }
            isShowApprovalStatus={
              isShowApprovalStatus &&
              isApprovalFlowEnabled &&
              !isApprovedStatus &&
              !isPreExistedStatus &&
              (isUserCanDoAction || isReviewerUser)
            }
            approvalStatus={t(Object.values(approvalStatuses).find(({ TYPE }) => TYPE === video?.approvalStatus)?.DESCRIPTION)}
          />
        )}
        <CardFooter
          className={cx(styles.footerCard)}
          onMouseEnter={() => toggleMouseEnterTitle(videoIndex)}
          onMouseLeave={() => toggleMouseLeaveTitle(videoIndex)}
        >
          {renameVideoData && renameVideoData[video.id] ? (
            <input
              autoFocus
              className={styles.rename}
              maxLength={MAX_NAME_LENGTH}
              type="text"
              onChange={e => onRenameInputChange(e, video.id)}
              value={renameVideoData[video.id].name}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  renameVideoAction(video.id);
                }
              }}
              onBlur={() => renameVideoAction(video.id)}
            />
          ) : (
            <div className={cx(styles.titleContainer, { [styles.squeezeTitle]: squeezeCard })}>
              <>
                <Highlighter
                  highlightClassName="wordHighlighter"
                  searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                  autoEscape
                  textToHighlight={video.name}
                  className={styles.highlighterCard}
                />
              </>
              <Tooltip isHover={titleHover.isHover} fullwidth={true} moveable={false}>
                <Highlighter
                  highlightClassName="wordHighlighter"
                  searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                  autoEscape
                  textToHighlight={renameVideoData[video.id] ? renameVideoData[video.id].name : video.name}
                  className={styles.hoverText}
                />
              </Tooltip>
            </div>
          )}
          <h3>
            {t('general.date', { date: new Date(`${video.createDate}`) })}
            <span>{video.source !== 'web' && <span style={{ textTransform: 'capitalize' }}> | {video.source}</span>}</span>
          </h3>
        </CardFooter>
      </Card>
      {isMobileView && (
        <Modal open={showMenu.uuid === video.id && showMenu.flag} onClose={toggleShowMenu} focusTrapped={false}>
          <div className={styles.menuContainer}>
            <CardMenu
              menuRef={menuRef}
              listMode={listMode}
              item={video}
              itemIndex={videoIndex}
              toggleShowMenu={toggleShowMenu}
              moveItem={isUserCanDoAdvancedAction && moveVideoAction}
              renameItem={
                !isProcessing &&
                (!isApprovalFlowEnabled || (!isInReviewStatus && !isApprovedStatus)) &&
                isUserCanDoAction &&
                renameVideo
              }
              handleCopyClick={
                !isProcessing && (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && !video.draft && handleCopyClick
              }
              shareItem={
                !isProcessing && (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && !video.draft
                  ? handleShareVideo
                  : undefined
              }
              removeFromReview={!isProcessing && isApprovalFlowEnabled && isInReviewStatus && isUserCanDoAction && removeFromReview}
              duplicateItem={!isProcessing && (video.author.username === user.username || isUserHasEditingRights) && duplicateVideo}
              downloadItem={
                !isProcessing && {
                  action: (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && !video.draft && downloadVideo,
                }
              }
              archiveItem={isVideoAuthor && archiveVideos}
              deleteVideo={isUserCanDoAdvancedAction && deleteVideo}
              unarchiveItem={isUserCanDoAction && unarchiveVideo}
              approveVideo={!isProcessing && approveVideo}
              declineVideo={!isProcessing && declineVideo}
              revokeVideo={!isProcessing && revokeVideo}
            />
          </div>
        </Modal>
      )}
    </Col>
  );
};

export default CardCol;
